import { i18n }                  from '../../../shared/i18n/i18n.js'
import { formatPriceLabelShort } from '../../../shared/utils/priceUtils.js'

import { FILTER_IDS, COMPONENT_TYPES } from '../constants/filterConstants.js'

// ── Formatters ────────────────────────────────────────────────────────────────

/**
 * Generates the compact range label shown next to the slider name in the main bar.
 * e.g.  rooms → "od 2 do 4"
 *       area  → "od 40 m² do 90 m²"
 *       price → "od 400 tys. do 1,20 mln"
 *
 * @param {string}           id    - filter id
 * @param {[number, number]} value - current [lo, hi]
 * @returns {string}
 */
export function formatMainBarLabel(id, value) {
  if (!value) return ''
  const [min, max] = value
  if (id === FILTER_IDS.AREA)  return i18n.t('catalog.range_area',  { min, max })
  if (id === FILTER_IDS.PRICE_RANGE || id === FILTER_IDS.PRICE_INPUT) return i18n.t('catalog.range_price', { min: formatPriceLabelShort(min), max: formatPriceLabelShort(max) })
  return i18n.t('catalog.range_default', { min, max })
}

/**
 * Formats a single value for a flat-range slider (min === max).
 *
 * @param {string} id
 * @param {number} val
 * @returns {string}
 */
export function formatSingleVal(id, val) {
  if (val == null) return ''
  if (id === FILTER_IDS.AREA)  return `${val} m²`
  if (id === FILTER_IDS.PRICE_RANGE || id === FILTER_IDS.PRICE_INPUT) return formatPriceLabelShort(val)
  return String(val)
}

// ── Multi-select option builder ───────────────────────────────────────────────

/**
 * Derives { value, label }[] option list for a MultiSelect filter from
 * the full filters range built by buildInitialFilters / useFilters.
 *
 * Option labels are resolved via i18n using the convention:
 *   `{entry.labelKey}_{value}` → e.g. 'catalog.exposition_N' → "Płn."
 * Status is special-cased to use 'status.{value}' (shared namespace).
 * Falls back to the raw string value for dynamic or unknown keys (e.g. building IDs).
 *
 * Handles four distinct range shapes:
 *  - strict-match  { selected: string[], strict: bool }   → exposition, external_areas …
 *  - insolation    [[lo, hi], …]                          → encode each pair as JSON string
 *  - tiers         number[]                               → 0 = Parter, N = Piętro N
 *  - standard      string[] | number[]                    → status, buildings, unit_levels …
 *
 * @param {object} entry            - filterRegistry entry ({ id, labelKey, … })
 * @param {object} fullFiltersRange - from buildInitialFilters / useFilters
 * @returns {{ value: string, label: string }[]}
 */
export function getMultiSelectOptions(entry, fullFiltersRange) {
  const { id, labelKey, optionLabelPrefix } = entry
  const range = fullFiltersRange?.[id]

  // Helper: resolve option label via i18n; fall back to raw value string.
  // Priority: optionLabelPrefix (compartments.*) > status namespace > labelKey prefix.
  const optLabel = (v) => {
    const key = optionLabelPrefix
      ? `${optionLabelPrefix}.${v}`            // e.g. 'compartments.idBalcony'
      : id === FILTER_IDS.STATUS
        ? `status.${v}`                        // e.g. 'status.available'
        : `${labelKey}_${v}`                   // e.g. 'catalog.exposition_N'
    const translated = i18n.t(key)
    return translated !== key ? translated : String(v)
  }

  // Strict-match shape: { selected: [...], strict: bool, options?: [...] }
  // options (when present) provides the dropdown choices; selected is the active state.
  if (!Array.isArray(range) && Array.isArray(range?.selected)) {
    const values = range.options ?? range.selected
    return values.map(v => ({
      value: String(v),
      label: optLabel(v),
    }))
  }

  // Insolation: array of bucket strings ('null' | '0'…'10')
  if (id === FILTER_IDS.INSOLATION && Array.isArray(range) && typeof range[0] === 'string') {
    return range.map(bucket => ({
      value: bucket,
      label: bucket === 'null'
        ? i18n.t('catalog.insolation_null')
        : i18n.t('catalog.insolation_from', { h: bucket }),
    }))
  }

  // Category: singular form from shared product_category_strings namespace
  if (id === FILTER_IDS.CATEGORY && Array.isArray(range)) {
    return range.map(v => ({
      value: String(v),
      label: i18n.t(`product_category_strings.${v}_one`),
    }))
  }

  // Compare area: decile buckets 'p0'–'p9' — labels from catalog.compare_area_p{n}
  if (id === FILTER_IDS.COMPARE_AREA && Array.isArray(range)) {
    return range.map(v => ({
      value: String(v),
      label: i18n.t(`catalog.compare_area_${v}`),
    }))
  }

  // Tiers: numeric floor indices — label is just the number (0 → "Parter")
  if (id === FILTER_IDS.TIERS && Array.isArray(range)) {
    return range.map(v => ({
      value: String(v),
      label: v === 0 ? i18n.t('catalog.tiers_groundfloor') : String(v),
    }))
  }

  // Standard string[] or number[]
  if (Array.isArray(range)) {
    return range.map(v => ({
      value: String(v),
      label: optLabel(v),
    }))
  }

  return []
}

/**
 * Returns true when the filter's UI component carries its own visible label
 * (MultiSelect summary trigger, TextSearch placeholder), making the external
 * FilterCard label redundant.
 *
 * Use at call sites to set `hideLabel` on FilterCard.
 *
 * @param {object} entry - FILTER_REGISTRY entry
 * @returns {boolean}
 */
export function isSelfLabeled(entry) {
  return (
    entry?.component === COMPONENT_TYPES.MULTI_SELECT ||
    entry?.component === COMPONENT_TYPES.TEXT_SEARCH
  )
}
